import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { inject, pageview } from '@vercel/analytics';
import { Button } from '@/components/ui/button';
import { OPEN_COOKIE_SETTINGS_EVENT, useConsent } from '../hooks/useConsent';

const ANALYTICS_SCRIPT_SELECTOR = 'script[src*="/_vercel/insights/script.js"]';

export default function ConsentBanner() {
  const { consent, setConsent } = useConsent();
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const openSettings = () => setSettingsOpen(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  useEffect(() => {
    if (consent !== 'accepted' || typeof window === 'undefined') return;

    (window as typeof window & { __analyticsLoaded?: boolean }).__analyticsLoaded = true;

    if (!document.head.querySelector(ANALYTICS_SCRIPT_SELECTOR)) {
      inject({
        disableAutoTrack: true,
      });
    }
  }, [consent]);

  useEffect(() => {
    if (consent !== 'accepted' || typeof window === 'undefined') return;

    pageview({
      route: location.pathname,
      path: `${location.pathname}${location.search}`,
    });
  }, [consent, location.pathname, location.search]);

  if (consent !== 'unknown' && !settingsOpen) return null;

  const chooseConsent = (nextConsent: 'accepted' | 'rejected') => {
    setConsent(nextConsent);
    setSettingsOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Analytics consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card px-4 py-4 text-card-foreground shadow-lg"
    >
      <div className="mx-auto flex max-w-4xl flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <p className="font-medium">Analytics preferences</p>
          <p className="text-sm text-muted-foreground">
            We use basic, privacy-friendly analytics only with your permission.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={() => chooseConsent('rejected')}>Reject</Button>
          <Button size="sm" onClick={() => chooseConsent('accepted')}>Accept</Button>
        </div>
      </div>
    </div>
  );
}
